import type { GalleryItem } from '@mtc/shared';
import { useCallback, useEffect, useState } from 'react';
import { StateMessage } from '../components/StateMessage';
import { useGallery } from '../data/queries';
import { useSEO } from '../hooks/useSEO';
import { trackEvent } from '../lib/track';

export function Galerie() {
  const galleryQ = useGallery();
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  useSEO({
    title: 'Galerie — MTC',
    description:
      "Photos des sessions précédentes du Moroccan Training Center — ateliers hands-on, masterclasses et moments forts en clinique.",
  });

  const items = galleryQ.data ?? [];
  const close = useCallback(() => setActiveIndex(null), []);
  const next = useCallback(
    () => setActiveIndex((i) => (i === null ? null : (i + 1) % items.length)),
    [items.length],
  );
  const prev = useCallback(
    () => setActiveIndex((i) => (i === null ? null : (i - 1 + items.length) % items.length)),
    [items.length],
  );

  // Keyboard navigation in lightbox.
  useEffect(() => {
    if (activeIndex === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowRight') next();
      else if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [activeIndex, close, next, prev]);

  if (galleryQ.isLoading) return <StateMessage variant="loading" />;
  if (galleryQ.error) return <StateMessage variant="error" message={String(galleryQ.error)} />;

  return (
    <div style={{ padding: '64px 32px 96px', maxWidth: 1320, margin: '0 auto' }}>
      <header style={{ marginBottom: 48 }}>
        <span className="mtc-eyebrow">Vie du centre</span>
        <h1
          style={{
            fontFamily: 'var(--mtc-font-display)',
            fontSize: 'clamp(40px, 5vw, 72px)',
            fontWeight: 400,
            lineHeight: 1.0,
            letterSpacing: '-0.03em',
            margin: '12px 0 16px',
          }}
        >
          Galerie
        </h1>
        <p
          style={{
            fontSize: 'clamp(15px, 1.2vw, 17px)',
            lineHeight: 1.5,
            color: 'rgba(15,25,22,0.65)',
            maxWidth: 640,
            margin: 0,
          }}
        >
          Moments capturés lors des sessions MTC — ateliers cliniques, manipulations sur modèles,
          coaching en cabinet.
        </p>
      </header>

      {items.length === 0 ? (
        <StateMessage
          variant="empty"
          message="La galerie est en cours de constitution. Revenez bientôt."
        />
      ) : (
        <div
          style={{
            columnCount: 3,
            columnGap: 12,
          }}
          className="gallery-grid"
        >
          {items.map((item, i) => (
            <button
              type="button"
              key={item.id}
              onClick={() => {
                trackEvent('gallery_open');
                setActiveIndex(i);
              }}
              style={{
                all: 'unset',
                cursor: 'zoom-in',
                display: 'block',
                breakInside: 'avoid',
                marginBottom: 12,
                borderRadius: 8,
                overflow: 'hidden',
                position: 'relative',
              }}
              aria-label={item.caption || (item.kind === 'video' ? 'Vidéo MTC' : 'Photo MTC')}
            >
              {item.kind === 'video' ? (
                <>
                  <video
                    // First-frame poster only — never preload the whole file
                    // for grid thumbnails (50MB × N would be brutal).
                    src={`${item.url}#t=0.1`}
                    preload="metadata"
                    muted
                    playsInline
                    style={{
                      width: '100%',
                      height: 'auto',
                      display: 'block',
                      transition: 'transform 0.4s cubic-bezier(0.2,0.8,0.2,1)',
                    }}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      display: 'grid',
                      placeItems: 'center',
                      pointerEvents: 'none',
                    }}
                  >
                    <div
                      style={{
                        width: 56,
                        height: 56,
                        borderRadius: 999,
                        background: 'rgba(0,0,0,0.55)',
                        color: 'white',
                        fontSize: 22,
                        display: 'grid',
                        placeItems: 'center',
                        backdropFilter: 'blur(4px)',
                      }}
                    >
                      ▶
                    </div>
                  </div>
                </>
              ) : (
                <img
                  src={item.url}
                  alt={item.caption ?? ''}
                  loading="lazy"
                  style={{
                    width: '100%',
                    height: 'auto',
                    display: 'block',
                    transition: 'transform 0.4s cubic-bezier(0.2,0.8,0.2,1)',
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLImageElement).style.transform = 'scale(1.03)';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLImageElement).style.transform = '';
                  }}
                />
              )}
              {(item.caption || item.eventLabel) && (
                <div
                  style={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    bottom: 0,
                    padding: '14px 14px 12px',
                    background:
                      'linear-gradient(to top, rgba(0,0,0,0.55), rgba(0,0,0,0))',
                    color: 'white',
                    fontSize: 12,
                    lineHeight: 1.35,
                    opacity: 0,
                    transition: 'opacity 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLDivElement).style.opacity = '1';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLDivElement).style.opacity = '0';
                  }}
                >
                  {item.caption}
                  {item.eventLabel && (
                    <div style={{ fontSize: 10, opacity: 0.75, marginTop: 2 }}>
                      {item.eventLabel}
                    </div>
                  )}
                </div>
              )}
            </button>
          ))}
        </div>
      )}

      {activeIndex !== null && items[activeIndex] && (
        <Lightbox
          item={items[activeIndex]!}
          onClose={close}
          onNext={next}
          onPrev={prev}
          counter={`${activeIndex + 1} / ${items.length}`}
        />
      )}

      <style>{`
        @media (max-width: 880px) { .gallery-grid { column-count: 2 !important; } }
        @media (max-width: 540px) { .gallery-grid { column-count: 1 !important; } }
      `}</style>
    </div>
  );
}

function Lightbox({
  item,
  onClose,
  onNext,
  onPrev,
  counter,
}: {
  item: GalleryItem;
  onClose: () => void;
  onNext: () => void;
  onPrev: () => void;
  counter: string;
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100,
        background: 'rgba(15,25,22,0.92)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onPrev();
        }}
        aria-label="Image précédente"
        style={lightboxArrowStyle('left')}
      >
        ‹
      </button>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onNext();
        }}
        aria-label="Image suivante"
        style={lightboxArrowStyle('right')}
      >
        ›
      </button>
      <button
        type="button"
        onClick={onClose}
        aria-label="Fermer"
        style={{
          position: 'absolute',
          top: 20,
          right: 24,
          width: 40,
          height: 40,
          borderRadius: 999,
          background: 'rgba(255,255,255,0.1)',
          color: 'white',
          border: 'none',
          fontSize: 22,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        ×
      </button>
      <div
        style={{ position: 'absolute', top: 24, left: 32, color: 'rgba(255,255,255,0.7)', fontFamily: 'var(--mtc-font-mono)', fontSize: 11, letterSpacing: '0.15em' }}
      >
        {counter}
      </div>
      {item.kind === 'video' ? (
        <video
          src={item.url}
          controls
          autoPlay
          playsInline
          onClick={(e) => e.stopPropagation()}
          style={{
            maxWidth: '92vw',
            maxHeight: '85vh',
            borderRadius: 4,
            boxShadow: '0 24px 80px rgba(0,0,0,0.5)',
            background: 'black',
          }}
        />
      ) : (
        <img
          src={item.url}
          alt={item.caption ?? ''}
          onClick={(e) => e.stopPropagation()}
          style={{
            maxWidth: '92vw',
            maxHeight: '85vh',
            objectFit: 'contain',
            borderRadius: 4,
            boxShadow: '0 24px 80px rgba(0,0,0,0.5)',
          }}
        />
      )}
      {(item.caption || item.eventLabel) && (
        <div
          style={{
            position: 'absolute',
            bottom: 24,
            left: 0,
            right: 0,
            textAlign: 'center',
            color: 'rgba(255,255,255,0.85)',
            fontSize: 14,
            padding: '0 32px',
          }}
        >
          {item.caption}
          {item.eventLabel && (
            <div style={{ fontSize: 11, opacity: 0.65, marginTop: 4, fontFamily: 'var(--mtc-font-mono)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
              {item.eventLabel}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function lightboxArrowStyle(side: 'left' | 'right'): React.CSSProperties {
  return {
    position: 'absolute',
    top: '50%',
    [side]: 10,
    transform: 'translateY(-50%)',
    width: 44,
    height: 44,
    borderRadius: 999,
    background: 'rgba(255,255,255,0.1)',
    color: 'white',
    border: 'none',
    fontSize: 28,
    lineHeight: 1,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };
}
